'use client';

import { Z_INDEX } from '@/constants';
import { menuItems } from '@/data/globals.data';
import { cn } from '@/lib/utils';
import { useBannerList } from '@/services/query/banner.query';
import { BannerPosition } from '@/services/types/banner.type';
import { MenuItem } from '@/services/types/globals.type';
import { StoreDomainConfigEntity } from '@/services/types/store.type';
import { useModalStore } from '@/store/modal.store';
import { useSidebarStore } from '@/store/sidebar.store';
import clsx from 'clsx';
import { Menu } from 'lucide-react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import ImageOptimizer from './image-optimizer';
import SectionTabs from './section-tabs';
// 위 예시에서는 next/image 사용, 혹은 직접 만든 ImageOptimizer 컴포넌트를 써도 됩니다.

export default function Header({
  storeDomainConfig,
}: {
  storeDomainConfig: StoreDomainConfigEntity;
}) {
  const { open: openModal } = useModalStore();
  const { open: openSidebar } = useSidebarStore();
  const pathname = usePathname();

  const { data: bannerList } = useBannerList({
    position: BannerPosition.HEADER_TOP,
  });
  const pcHeaderTopBannerHref = bannerList?.data?.pc?.[0]?.href || '';
  const pcHeaderTopBannerUpfilePath =
    bannerList?.data?.pc?.[0]?.upfilePath || '';
  const mobileHeaderTopBannerHref = bannerList?.data?.mobile?.[0]?.href || '';
  const mobileHeaderTopBannerUpfilePath =
    bannerList?.data?.mobile?.[0]?.upfilePath || '';

  // 현재 경로에 따라 탭 결정
  const getTabs = (): MenuItem[] | null => {
    const target = menuItems.find(
      (item) => item.parentHref && pathname?.startsWith(item.parentHref),
    );
    return target?.submenu ?? null;
  };
  const tabs = getTabs();

  return (
    <>
      <header
        className="desktop:max-w-screen mobile-w fixed left-1/2 flex w-screen -translate-x-1/2 flex-col"
        style={{
          zIndex: Z_INDEX.Header,
        }}
      >
        <div className="bg-main-yellow h-14 w-full">
          {/*헤더 위 상단배너 - 웹*/}
          <Link
            href={pcHeaderTopBannerHref}
            className="desktop:block hidden h-full w-full"
          >
            <div className="relative h-full">
              {/* <div className="relative mt-4 h-5"> */}
              <ImageOptimizer
                src={pcHeaderTopBannerUpfilePath}
                alt="이벤트"
                fill
                className="object-contain"
              />
            </div>
          </Link>
          {/*헤더 위 상단배너 - 모바일*/}
          <Link
            href={mobileHeaderTopBannerHref}
            className="desktop:hidden h-full w-full"
          >
            <div className="relative h-full">
              {/* <div className="relative mt-5 h-4"> */}
              <ImageOptimizer
                src={mobileHeaderTopBannerUpfilePath}
                alt="이벤트"
                fill
                className="object-contain"
              />
            </div>
          </Link>
        </div>

        <div className="desktop:w-full desktop:flex desktop:justify-center border-stroke-white border-b bg-white">
          <div
            className={clsx(
              'desktop:web-w desktop:w-full border-stroke-white desktop:px-0 flex h-20 items-center justify-between bg-white px-6 py-3',
            )}
          >
            <div className="flex items-center">
              <Link href="/" className="flex items-center">
                <div className="desktop:w-[162px] desktop:h-[26px] relative h-[30px] w-[186px]">
                  {/* 로고 이미지 */}
                  <ImageOptimizer
                    src={
                      storeDomainConfig.logoUrl
                        ? storeDomainConfig.logoUrl
                        : '/images/logo.webp'
                    }
                    alt="드라이빙존 운전면허 로고"
                    fill
                  />
                </div>
              </Link>
            </div>
            <nav className="desktop:flex hidden">
              <ul className="flex">
                {menuItems.map((item) => (
                  <li key={item.label}>
                    <ul className="flex w-full">
                      <li key={item.label}>
                        <Link
                          href={item.href || ''}
                          className={cn(
                            'text-level-3 typo-17 block px-4.5 py-2.5',
                            pathname?.startsWith(item.href)
                              ? 'font-extrabold'
                              : 'font-semibold',
                          )}
                        >
                          {item.label}
                        </Link>
                      </li>
                    </ul>
                  </li>
                ))}
              </ul>
            </nav>
            <div className="flex items-center gap-2.5">
              <button
                onClick={() => openModal()}
                className="bg-main-orange desktop:px-[22px] desktop:py-[11px] desktop:rounded-sm desktop:flex hidden shrink-0 cursor-pointer rounded-lg px-4 py-3"
              >
                <span className="typo-16 desktop:typo-18 font-bold text-white">
                  상담 예약
                </span>
              </button>

              <Link href="/franchise">
                <button className="bg-main-darkgray desktop:px-[22px] desktop:py-[11px] desktop:rounded-sm flex shrink-0 cursor-pointer rounded-lg px-4 py-3">
                  <span className="typo-16 text-sub-white desktop:typo-18 font-bold">
                    가맹 문의
                  </span>
                </button>
              </Link>

              <button
                onClick={openSidebar}
                className="desktop:hidden cursor-pointer p-1 focus:outline-none"
                aria-label="Menu"
              >
                <Menu className="h-6 w-6 text-gray-800" />
              </button>
            </div>
          </div>
        </div>
        {tabs && (
          <SectionTabs
            tabs={tabs}
            className="desktop:flex mb-6 hidden"
            data-section-tabs="true"
          />
        )}
        {/* MobileSidebar는 이제 MainLayout에서 관리 */}
      </header>
      <div className="h-34" />
      {tabs && <div className="desktop:block hidden h-14"></div>}
    </>
  );
}
